import "./style.css";
import Joystick from "rc-joystick";
import { useGame } from "./stores/useGame";
import useDeviceType from "./components/hooks/useDeviceType";
import { useEffect, useRef, useState } from "react";
import { addEffect } from "@react-three/fiber";
import { handleButtonActions } from "./utils/buttonActions";
import { COLORS } from "./constants/colors";
import { useLoading } from "./stores/useLoading";

// Цитаты великих людей
const quotes = [
  {
    text: "The only way to do great work is to love what you do.",
    author: "Steve Jobs",
  },
  {
    text: "Innovation distinguishes between a leader and a follower.",
    author: "Steve Jobs",
  },
  {
    text: "Have the courage to follow your heart and intuition. They somehow already know what you truly want to become. Everything else is secondary.",
    author: "Steve Jobs",
  },
  {
    text: "The future belongs to those who believe in the beauty of their dreams.",
    author: "Eleanor Roosevelt",
  },
  {
    text: "We are what we repeatedly do. Excellence, then, is not an act, but a habit.",
    author: "Aristotle",
  },
  {
    text: "The way to get started is to quit talking and begin doing.",
    author: "Walt Disney",
  },
  {
    text: "Don't be afraid to give up the good to go for the great.",
    author: "John D. Rockefeller",
  },
  {
    text: "If you want to lift yourself up, lift up someone else.",
    author: "Booker T. Washington",
  },
  {
    text: "The only limit to our realization of tomorrow will be our doubts of today.",
    author: "Franklin D. Roosevelt",
  },
  {
    text: "The greatest glory in living lies not in never falling, but in rising every time we fall.",
    author: "Nelson Mandela",
  },
];

export default function Interface() {
  const { setDirection } = useGame();
  const deviceType = useDeviceType();
  const time = useRef(0);
  const [selectedQuote, setSelectedQuote] = useState(null);

  // Состояние загрузки Experience
  const isExperienceReady = useLoading((state) => state.isExperienceReady);
  const shouldShowExperience = useLoading(
    (state) => state.shouldShowExperience
  );
  const setShouldShowExperience = useLoading(
    (state) => state.setShouldShowExperience
  );

  // Выбираем случайную цитату при монтировании компонента
  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * quotes.length);
    setSelectedQuote(quotes[randomIndex]);
  }, []);

  const handleStartClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isExperienceReady) {
      setShouldShowExperience(true);
    }
  };

  const handleMove = (direction) => {
    setDirection(direction);
  };

  const handleStop = () => {
    setDirection(null);
  };

  const startRace = useGame((state) => state.startRace);
  const buttonStates = useGame((state) => state.buttonStates);
  const isStartButtonActive = useGame((state) => state.isStartButtonActive);
  const triggerReset = useGame((state) => state.triggerReset);

  // Обработчик клика по кнопке GO для мобильных устройств
  const handleGoClick = () => {
    const state = useGame.getState();
    handleButtonActions(state);
  };

  // Обработчик клика по кнопке R для сброса машинки
  const handleResetClick = () => {
    triggerReset();
  };

  useEffect(() => {
    if (deviceType === "desktop") {
      setDirection(undefined);
    }
  }, [deviceType]);

  // Предотвращаем прокрутку страницы на мобильных устройствах
  useEffect(() => {
    if (deviceType === "mobile" || deviceType === "tablet") {
      // Предотвращаем прокрутку страницы
      const preventScroll = (e) => {
        e.preventDefault();
      };

      // Предотвращаем зум
      const preventZoom = (e) => {
        if (e.touches.length > 1) {
          e.preventDefault();
        }
      };

      // Предотвращаем контекстное меню
      const preventContextMenu = (e) => {
        e.preventDefault();
      };

      // Скрываем элементы браузера и переводим в полноэкранный режим
      const hideBrowserUI = () => {
        // Скрываем адресную строку и элементы управления
        if (window.navigator && window.navigator.standalone !== true) {
          // Для iOS Safari
          if (
            window.navigator.userAgent.includes("Safari") &&
            !window.navigator.userAgent.includes("Chrome")
          ) {
            document.body.style.height = "100vh";
            document.body.style.height = "-webkit-fill-available";
          }
        }

        // Скрываем элементы браузера через CSS
        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";

        // Принудительно скрываем адресную строку
        window.scrollTo(0, 1);
        setTimeout(() => window.scrollTo(0, 0), 0);
      };

      // Добавляем обработчики событий
      document.addEventListener("touchmove", preventScroll, { passive: false });
      document.addEventListener("touchstart", preventZoom, { passive: false });
      document.addEventListener("contextmenu", preventContextMenu);
      document.addEventListener("gesturestart", preventZoom, {
        passive: false,
      });
      document.addEventListener("gesturechange", preventZoom, {
        passive: false,
      });
      document.addEventListener("gestureend", preventZoom, { passive: false });

      // Скрываем элементы браузера
      hideBrowserUI();

      // Периодически скрываем элементы браузера
      const hideInterval = setInterval(hideBrowserUI, 1000);

      // Очищаем обработчики при размонтировании
      return () => {
        document.removeEventListener("touchmove", preventScroll);
        document.removeEventListener("touchstart", preventZoom);
        document.removeEventListener("contextmenu", preventContextMenu);
        document.removeEventListener("gesturestart", preventZoom);
        document.removeEventListener("gesturechange", preventZoom);
        document.removeEventListener("gestureend", preventZoom);
        clearInterval(hideInterval);
      };
    }
  }, [deviceType]);

  useEffect(() => {
    const unsubcribeTime = addEffect(() => {
      const state = useGame.getState();

      let elapsedTime = 0;
      if (state.startRace === true) {
        elapsedTime = Date.now() - state.startTime;
      } else if (state.startRace === false) {
        elapsedTime = state.endTime - state.startTime;
      }
      if (time.current) {
        time.current.textContent = (elapsedTime / 1000).toFixed(2);
      }
    });
    return () => unsubcribeTime();
  }, []);

  // Проверяем, активна ли любая кнопка
  const isAnyButtonActive =
    Object.values(buttonStates).some(Boolean) || isStartButtonActive;

  return (
    <>
      {/* Зеленый фон, цитата и кнопка загрузки */}
      {!shouldShowExperience && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: COLORS.background,
            zIndex: 10000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "auto",
            padding: "20px",
          }}
        >
          {/* Цитата в центре */}
          {selectedQuote && (
            <div
              style={{
                maxWidth: "800px",
                textAlign: "center",
                margin: "0 auto 60px auto",
                padding: "0 20px",
                paddingRight: "60px",
              }}
            >
              <p
                style={{
                  color: "white",
                  fontSize: "24px",
                  fontWeight: "400",
                  lineHeight: "1.6",
                  margin: "0 0 20px 0",
                  fontStyle: "italic",
                }}
              >
                "{selectedQuote.text}"
              </p>
              <p
                style={{
                  color: "white",
                  fontSize: "18px",
                  fontWeight: "300",
                  margin: 0,
                  opacity: 0.9,
                }}
              >
                {selectedQuote.author}
              </p>
            </div>
          )}

          {/* Кнопка загрузки */}
          <button
            onClick={handleStartClick}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleStartClick(e);
            }}
            disabled={!isExperienceReady}
            style={{
              padding: "15px 30px",
              fontSize: "18px",
              fontWeight: "bold",
              backgroundColor: isExperienceReady ? "#FF9800" : "#cccccc",
              color: isExperienceReady ? "white" : "#666666",
              border: "none",
              borderRadius: "8px",
              cursor: isExperienceReady ? "pointer" : "not-allowed",
              opacity: isExperienceReady ? 1 : 0.6,
              pointerEvents: "auto",
              userSelect: "auto",
              WebkitUserSelect: "auto",
              touchAction: "manipulation",
              outline: "none",
              marginRight: "30px",
            }}
            onMouseEnter={(e) => {
              if (isExperienceReady) {
                e.target.style.backgroundColor = "#ffa726";
                e.target.style.transform = "scale(1.05)";
              }
            }}
            onMouseLeave={(e) => {
              if (isExperienceReady) {
                e.target.style.backgroundColor = "#FF9800";
                e.target.style.transform = "scale(1)";
              }
            }}
          >
            {isExperienceReady ? "Go!" : "Ready..."}
          </button>
        </div>
      )}

      <div className="interface">
        {startRace && (
          <div ref={time} className="time">
            0.00
          </div>
        )}

        {/* Показываем надпись "Press Enter to interact" только для десктопа */}
        {isAnyButtonActive && deviceType === "desktop" && (
          <div
            style={{
              position: "fixed",
              bottom: "20px",
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              color: "white",
              padding: "10px 20px",
              borderRadius: "8px",
              fontSize: "16px",
              fontFamily: "Arial, sans-serif",
              zIndex: 1000,
              pointerEvents: "none",
              border: "2px solid #4CAF50",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
            }}
          >
            🎮 Press <strong>Enter</strong> to interact
          </div>
        )}

        {(deviceType === "mobile" || deviceType === "tablet") && (
          <>
            <Joystick
              directionCount={1}
              baseRadius={50}
              controllerRadius={35}
              onDirectionChange={(direction) => {
                if (direction !== "Center") {
                  handleMove(direction);
                } else {
                  // Когда джойстик в центре, очищаем направление для плавного затухания
                  setDirection(null);
                }
              }}
            />

            {/* Кнопка R для сброса машинки (всегда видна) */}
            <button
              onClick={handleResetClick}
              style={{
                position: "fixed",
                top: "40px",
                right: "40px",
                width: "50px",
                height: "50px",
                borderRadius: "50%",
                backgroundColor: "#808080",
                color: "white",
                border: "2px solid #fff",
                fontSize: "14px",
                fontWeight: "900",
                fontFamily: "Arial, sans-serif",
                cursor: "pointer",
                zIndex: 1000,
                boxShadow:
                  "0 4px 12px rgba(128, 128, 128, 0.4), 0 0 0 2px rgba(128, 128, 128, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                userSelect: "none",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
                letterSpacing: "0.5px",
              }}
              onMouseDown={(e) => {
                e.target.style.transform = "scale(0.9)";
                e.target.style.backgroundColor = "#666666";
                e.target.style.boxShadow =
                  "0 2px 8px rgba(128, 128, 128, 0.6), 0 0 0 1px rgba(128, 128, 128, 0.2)";
              }}
              onMouseUp={(e) => {
                e.target.style.transform = "scale(1)";
                e.target.style.backgroundColor = "#808080";
                e.target.style.boxShadow =
                  "0 4px 12px rgba(128, 128, 128, 0.4), 0 0 0 2px rgba(128, 128, 128, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "scale(1)";
                e.target.style.backgroundColor = "#808080";
                e.target.style.boxShadow =
                  "0 4px 12px rgba(128, 128, 128, 0.4), 0 0 0 2px rgba(128, 128, 128, 0.1)";
              }}
              onTouchStart={(e) => {
                e.target.style.transform = "scale(0.9)";
                e.target.style.backgroundColor = "#666666";
                e.target.style.boxShadow =
                  "0 2px 8px rgba(128, 128, 128, 0.6), 0 0 0 1px rgba(128, 128, 128, 0.2)";
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.target.style.transform = "scale(1)";
                e.target.style.backgroundColor = "#808080";
                e.target.style.boxShadow =
                  "0 4px 12px rgba(128, 128, 128, 0.4), 0 0 0 2px rgba(128, 128, 128, 0.1)";
                handleResetClick();
              }}
            >
              R
            </button>

            {/* Кнопка GO для мобильных устройств */}
            {isAnyButtonActive && (
              <button
                onClick={handleGoClick}
                style={{
                  position: "fixed",
                  bottom: "40px",
                  left: "40px",
                  width: "100px",
                  height: "100px",
                  borderRadius: "50%",
                  backgroundColor: COLORS.goButton,
                  color: "white",
                  border: "3px solid #fff",
                  fontSize: "20px",
                  fontWeight: "900",
                  fontFamily: "Arial, sans-serif",
                  cursor: "pointer",
                  zIndex: 1000,
                  boxShadow:
                    "0 8px 25px rgba(102, 126, 234, 0.4), 0 0 0 4px rgba(102, 126, 234, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  userSelect: "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  animation: "pulse 2s infinite",
                  textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
                  letterSpacing: "1px",
                }}
                onMouseDown={(e) => {
                  e.target.style.transform = "scale(0.9)";
                  e.target.style.backgroundColor = "#1a7ae6";
                  e.target.style.boxShadow =
                    "0 4px 15px rgba(30, 144, 255, 0.6), 0 0 0 2px rgba(30, 144, 255, 0.2)";
                }}
                onMouseUp={(e) => {
                  e.target.style.transform = "scale(1)";
                  e.target.style.backgroundColor = COLORS.goButton;
                  e.target.style.boxShadow =
                    "0 8px 25px rgba(30, 144, 255, 0.4), 0 0 0 4px rgba(30, 144, 255, 0.1)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "scale(1)";
                  e.target.style.backgroundColor = COLORS.goButton;
                  e.target.style.boxShadow =
                    "0 8px 25px rgba(30, 144, 255, 0.4), 0 0 0 4px rgba(30, 144, 255, 0.1)";
                }}
              >
                GO
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
}
